import React from 'react';
import { Flex, Gap, Select, Text } from '@common/components';
import { Colors, FontSize, FontWeight, Radius, Spacing } from '@common/tokens';
import type {
  ScheduleRoleType,
  ScheduleShiftTypeSectionType,
  ScheduleShiftTypeFilterType,
} from '@load-balancing/types';
import { ShiftCalendarLegend } from './_shift-calendar-legend';

/** Private filter/legend row rendered in the {@link ScheduleCalendar} header slot. */
export function ScheduleCalendarControls({
  roles,
  roleFilter,
  onRoleFilterChange,
  roleStatuses,
  shiftTypeSections,
  shiftTypeFilter,
  onShiftTypeFilterChange,
}: {
  roles: ScheduleRoleType[];
  roleFilter: ScheduleRoleType[];
  onRoleFilterChange?: (roles: ScheduleRoleType[]) => void;
  roleStatuses?: Record<ScheduleRoleType, number>;
  shiftTypeSections: ScheduleShiftTypeSectionType[];
  shiftTypeFilter: ScheduleShiftTypeFilterType;
  onShiftTypeFilterChange?: (filter: ScheduleShiftTypeFilterType) => void;
}) {
  const otherRolesNeedReview =
    roleFilter.length > 0 &&
    roles.some(
      (role) => !roleFilter.includes(role) && (roleStatuses?.[role] ?? 0) > 0
    );

  return (
    <Gap vertical size="sm">
      <Flex justify="space-between" align="center" wrap fullWidth>
        <Gap size="md" align="center" wrap>
          <Select
            mode="multiple"
            size="small"
            value={roleFilter}
            onChange={(value) =>
              onRoleFilterChange?.(value as ScheduleRoleType[])
            }
            style={{ minWidth: 240 }}
            placeholder="All roles"
            showSearch={false}
            maxTagCount={2}
            allowClear
            aria-label="Filter by role"
          >
            {roles.map((role) => {
              const pending = roleStatuses?.[role] ?? 0;
              return (
                <Select.Option key={role} value={role} label={role}>
                  <span className="schedule-calendar-controls__role-option">
                    <span>{role}</span>
                    {pending > 0 ? (
                      <span className="schedule-calendar-controls__role-badge">
                        {pending}
                      </span>
                    ) : null}
                  </span>
                </Select.Option>
              );
            })}
          </Select>

          <Select
            size="small"
            placeholder="All shifts"
            // Empty means "all shifts": map the `all` sentinel to a cleared
            // value, and a cleared value back to `all` for the consumer.
            value={shiftTypeFilter === 'all' ? undefined : shiftTypeFilter}
            onChange={(value) => onShiftTypeFilterChange?.(value ?? 'all')}
            style={{ minWidth: 130 }}
            allowClear
            aria-label="Filter by shift type"
          >
            {shiftTypeSections.map((section) => (
              <Select.Option key={section.key} value={section.key}>
                {section.label}
              </Select.Option>
            ))}
          </Select>
        </Gap>

        <ShiftCalendarLegend />
      </Flex>

      {otherRolesNeedReview ? (
        <div className="schedule-calendar-controls__hint">
          <Gap size="xs" align="center">
            <span className="schedule-calendar-controls__hint-dot" />
            <Text size="sm">Other roles need review</Text>
          </Gap>
        </div>
      ) : null}

      <style jsx>{`
        .schedule-calendar-controls__role-option {
          max-height: 16px;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          gap: ${Spacing.sm}px;
        }
        .schedule-calendar-controls__role-badge {
          min-width: 12px;
          height: 12px;
          padding: 0 4px;
          border-radius: ${Radius.md};
          background: ${Colors.sunDarker};
          color: ${Colors.white};
          font-size: ${FontSize.xs};
          font-weight: ${FontWeight.bold};
          line-height: 12px;
          text-align: center;
        }
        .schedule-calendar-controls__hint {
          padding: ${Spacing.xs}px ${Spacing.sm}px;
          border-radius: ${Radius.md};
          background: ${Colors.sunLightest};
        }
        .schedule-calendar-controls__hint-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: ${Colors.sunDarker};
        }
      `}</style>
    </Gap>
  );
}
